import { useEffect, useMemo, useState } from 'react'
import Layout from '@Components/Layout'
import { useAppDispatch, useAppSelector } from '@Hooks/ReduxHooks'
import { bootstrapAuthAction } from '@Features/Authentication/AuthenticationSlice/Authentication.slice'
import { getAssets, getEducationalInfo, getSettings, updateOnlineStatus } from '@Features/Settings/SettingsSlice/Settings.slice'
import { dashboardAPI, useGetUserProfileQuery } from '@Features/Dashboard/DashboardSlice/Dashboard.slice'
import landingPageApi from '@Features/LandingPage/LandingPageApi'
import classroomApi from '@Features/Classroom/ClassroomSlice/ClassroomSlice'
import { RoutingProvider } from './Providers/RoutingProvider/RoutingProvider'
import { SnackbarProvider } from '@eduact/ed-components'
import ApiProvider from '@Providers/ApiProvider/ApiProvider'
import EdSnackbarProvider from '@Providers/EdSnackbarProvider/EdSnackbarProvider'
import { checkRunningTest } from '@Redux/RunningTestSlice/RunningTestSlice'
import { useSearchParams } from 'react-router-dom'
import { createTheme, ThemeProvider } from '@eduact/student-theme'
import { Reset } from 'styled-reset'
import GlobalStyles from '@Styles/GlobalStyles'
import QuillContentStyles from '@Styles/QuillContentStyles'
import 'katex/dist/katex.min.css'
import { useDeviceInfo } from '@eduact/utils'
import LocalStorageKeys from '@Constants/LocalStorageKeys'

import { ThemeSelector } from '@Providers/ThemeSelector/ThemeSelector'
import { useBrowserTabInit } from '@Hooks/useBrowserTabInit'
import { useMarketingTags } from '@Hooks/useMarketingTags'
import WaselChat from '@Components/WaselChat/WaselChat'
import { setEmbed } from '@Redux/UtilsSlice/UtilsSlice'

/** Hidden longer than this → the session may have died meanwhile; re-ask. */
const REVALIDATE_AFTER_HIDDEN_MS = 5 * 60 * 1000

export default function App() {
	const dispatch = useAppDispatch()
	const { isAuth, authReady } = useAppSelector(state => state.auth)
	// keyName only: this call exists to mint and persist the device UUID in
	// localStorage. Do NOT add detectLocation here — it fires the browser
	// geolocation prompt on first paint of every route, logged in or not.
	useDeviceInfo({
		keyName: LocalStorageKeys.DeviceId,
	})

	useGetUserProfileQuery(undefined, {
		skip: !isAuth,
	})

	// Repair the profile entry once the server has answered.
	//
	// getUserProfile's queryFn reads `isAuth` from Redux and, when false, returns
	// null WITHOUT issuing a request — and RTK Query caches that null like any
	// other success. The skip above protects this call site, but LearningTimeWidget
	// subscribes with no skip and shares the cache key, so on a reload it runs the
	// queryFn while auth is still unresolved and poisons the shared entry. Nothing
	// invalidated 'Profile' afterwards, so the dashboard rendered with an empty
	// profile — looking signed out — for the life of the page.
	//
	// Invalidating is the same remedy VerifyDevice and ClassroomSlice already use,
	// and it repairs the entry whichever call site poisoned it. Fires at most once
	// per sign-in: the quiet revalidation below flips neither flag.
	//
	// The list invalidations ride along because this effect is the one place
	// every anonymous→authenticated transition passes — cold authed boot,
	// in-place login and signup, and the impersonation redeem. Login and signup
	// already invalidate these from their own submit handlers, but the redeem
	// has no form to do it from: the embed boots on the landing page, its
	// queries cache anonymous lists before the code is redeemed, and nothing
	// refreshed them afterwards. On a cold boot the entries don't exist yet, so
	// the extra dispatches are no-ops there.
	useEffect(() => {
		if (authReady && isAuth) {
			dispatch(dashboardAPI.util.invalidateTags(['Profile']))
			dispatch(landingPageApi.util.invalidateTags(['Classrooms', 'Instructors']))
			dispatch(classroomApi.util.invalidateTags([{ type: 'CLASSROOM' }]))
		}
	}, [authReady, isAuth])

	useBrowserTabInit({ auto: true })

	// GTM + all vendor pixels, injected once getSettings delivers the IDs the
	// admin entered on Settings → Marketing. Replaces the old hardcoded
	// FacebookPixel/ReactGA init that fired the legacy pixel on every brand.
	useMarketingTags()

	useEffect(() => {
		// Ask the server who we are. The session cookie is httpOnly, so unlike the
		// old localStorage check this cannot report a session the server rejected.
		// Skip when the layout is about to redeem an impersonation code. Both
		// resolve the same auth state and both fulfilled reducers overwrite `user`
		// and `isAuth`, so a late anonymous /auth/me could land after a successful
		// redemption and blank it — the admin's embedded portal would flip from
		// signed-in to signed-out for no visible reason. Redemption returns the
		// user itself, so nothing is lost by not asking twice.
		//
		// The skip requires BOTH params because Layout's redeem requires both: a
		// code without `embed` never dispatches the redeem, so skipping on the
		// code alone left authReady permanently unset on a hand-crafted
		// /login?impersonationCode=... link — and every authReady-gated view
		// waiting forever.
		if (!(params.get('impersonationCode') && params.get('embed'))) {
			dispatch(bootstrapAuthAction())
		}
		dispatch(getSettings())
		dispatch(getAssets())
		dispatch(getEducationalInfo())
		dispatch(checkRunningTest())
		dispatch(setEmbed(!!Boolean(params.get('embed'))))
		window.addEventListener('online', e => handleOnlineChange(true))
		window.addEventListener('offline', e => handleOnlineChange(false))
		return () => {
			window.removeEventListener('online', e => handleOnlineChange(true))
			window.removeEventListener('offline', e => handleOnlineChange(false))
		}
	}, [])

	// The boot answer above goes stale in a living tab: the session can die
	// server-side while the tab sits open, and a bfcache Back restores a
	// pre-death heap without re-running this mount. The hybrid public/logged-in
	// endpoints answer a dead cookie with guest-shaped 200s — never a 401 — so
	// nothing else ever corrects `isAuth`, and the app renders authed branches
	// against anonymous payloads. Re-ask at the two staleness points.
	useEffect(() => {
		const revalidate = () => dispatch(bootstrapAuthAction({ quiet: true }))
		const handlePageShow = (e: PageTransitionEvent) => {
			if (e.persisted) revalidate()
		}
		let hiddenAt: number | null = null
		const handleVisibility = () => {
			if (document.visibilityState === 'hidden') {
				hiddenAt = Date.now()
			} else if (hiddenAt !== null && Date.now() - hiddenAt >= REVALIDATE_AFTER_HIDDEN_MS) {
				hiddenAt = null
				revalidate()
			}
		}
		window.addEventListener('pageshow', handlePageShow)
		document.addEventListener('visibilitychange', handleVisibility)
		return () => {
			window.removeEventListener('pageshow', handlePageShow)
			document.removeEventListener('visibilitychange', handleVisibility)
		}
	}, [])

	const handleOnlineChange = (online: boolean) => {
		dispatch(updateOnlineStatus(online))
	}
	const [params] = useSearchParams()

	const isEmbed = useMemo(() => {
		return Boolean(params.get('embed'))
	}, [])

	return (
		<ThemeSelector>
			{(theme, setTheme) => {
				return (
					<>
						<ThemeProvider theme={theme} setTheme={setTheme}>
							<GlobalStyles />
							<QuillContentStyles />
							<Reset />
							<RoutingProvider>
								<SnackbarProvider>
									<EdSnackbarProvider>
										<ApiProvider>
											<Layout embed={isEmbed} />
											<WaselChat />
										</ApiProvider>
									</EdSnackbarProvider>
								</SnackbarProvider>
							</RoutingProvider>
						</ThemeProvider>
					</>
				)
			}}
		</ThemeSelector>
	)
}
